import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

export const SESSION_EXPIRED_MESSAGE =
  'Your session expired — please sign in again.';

/**
 * Thrown when a write is attempted without a usable Supabase session.
 *
 * This exists because an expired/stale token silently downgrades the
 * request to the anonymous role, and the only thing Postgres can say
 * back is a cryptic "new row violates row-level security policy"
 * (42501). Surfacing a typed error lets the UI show the actionable
 * "sign in again" message instead.
 */
export class SessionExpiredError extends Error {
  constructor(message: string = SESSION_EXPIRED_MESSAGE) {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

// Refresh slightly ahead of the real expiry so a request that's about to
// fire doesn't race the token going stale mid-flight.
const REFRESH_SKEW_MS = 60_000;

/**
 * Guarantee there's a valid, non-expired session before a write hits the
 * database. `getSession()` reads the cached token (and supabase-js will
 * auto-refresh it when possible); we additionally force a refresh when the
 * token is within {@link REFRESH_SKEW_MS} of expiring so long-idle tabs
 * don't send a just-expired JWT. Throws {@link SessionExpiredError} when
 * there's no recoverable session.
 *
 * Call this at the top of a mutation's `mutationFn` for writes where a
 * silent auth failure would otherwise surface as a confusing RLS error.
 */
export async function ensureActiveSession(): Promise<Session> {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw new SessionExpiredError();

  let session = data.session;
  if (!session) throw new SessionExpiredError();

  const expiresAtMs = session.expires_at ? session.expires_at * 1000 : 0;
  if (expiresAtMs && expiresAtMs - Date.now() < REFRESH_SKEW_MS) {
    const refreshed = await supabase.auth.refreshSession();
    if (refreshed.error || !refreshed.data.session) {
      throw new SessionExpiredError();
    }
    session = refreshed.data.session;
  }

  return session;
}

/**
 * Best-effort check used by the global mutation error handler: returns
 * true when there is no usable session at all (so a failed write was
 * almost certainly an auth problem, not a real permission denial).
 */
export async function hasNoActiveSession(): Promise<boolean> {
  try {
    const { data } = await supabase.auth.getSession();
    return !data.session;
  } catch {
    return true;
  }
}
